import { getIdToken } from "../auth";
import type { MeResponse } from "./user-doc";

function apiBase(): string {
  const url = import.meta.env.VITE_API_FUNCTION_URL as string | undefined;
  if (!url) throw new Error("VITE_API_FUNCTION_URL is not set.");
  return url.replace(/\/$/, "");
}

async function authHeaders(): Promise<HeadersInit> {
  const token = await getIdToken();
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

async function check(r: Response): Promise<Response> {
  if (!r.ok) {
    const body = await r.text().catch(() => r.statusText);
    throw new Error(`API ${r.status}: ${body.slice(0, 200)}`);
  }
  return r;
}

export async function signup(args: {
  email: string;
  password: string;
  fullName: string;
  inviteCode?: string;
}): Promise<{ userId: string; authUid: string; tier: "free" | "individual" }> {
  const r = await fetch(`${apiBase()}/signup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(args),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({ error: r.statusText }));
    throw Object.assign(new Error(body.error ?? "Signup failed"), { status: r.status, body });
  }
  return await r.json();
}

/**
 * Looks up the name + email an invite code was sent to, so the signup screen
 * can pre-fill them (not just the code) from a ?invite=CODE email link. Public
 * — called before the user has an account. Returns null for any code that
 * isn't a valid, unused, campaign-bound invite (the signup form just falls
 * back to blank fields in that case).
 */
export async function getInvitePreview(code: string): Promise<{ name: string; email: string } | null> {
  const r = await fetch(`${apiBase()}/invite-preview?code=${encodeURIComponent(code)}`);
  if (!r.ok) return null;
  return (await r.json()) as { name: string; email: string };
}

export async function getMe(): Promise<MeResponse> {
  const r = await check(await fetch(`${apiBase()}/me`, { headers: await authHeaders() }));
  return await r.json();
}

export async function acceptDataNotice(): Promise<void> {
  await check(
    await fetch(`${apiBase()}/me/accept-data-notice`, {
      method: "POST",
      headers: await authHeaders(),
    }),
  );
}

export type BillingPlan = "monthly" | "annual";

/** Create a Stripe Checkout session and return its hosted URL. */
export async function createCheckout(plan: BillingPlan = "monthly"): Promise<string> {
  const r = await check(
    await fetch(`${apiBase()}/stripe/checkout`, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({ plan }),
    }),
  );
  const { url } = (await r.json()) as { url: string };
  return url;
}

/**
 * Start Stripe setup-mode Checkout to validate + save a card (no charge).
 * Returns the hosted URL to redirect to, or null if a card is already on file.
 */
export async function createSetupCheckout(): Promise<string | null> {
  const r = await check(
    await fetch(`${apiBase()}/stripe/setup`, {
      method: "POST",
      headers: await authHeaders(),
    }),
  );
  const data = (await r.json()) as { url?: string; alreadyOnFile?: boolean };
  return data.url ?? null;
}

/**
 * Confirm the card directly with Stripe (used on return from card setup) so
 * cardOnFile is set without waiting for the webhook. Returns whether a card is
 * now on file.
 */
export async function confirmCard(): Promise<boolean> {
  const r = await check(
    await fetch(`${apiBase()}/stripe/confirm-card`, {
      method: "POST",
      headers: await authHeaders(),
    }),
  );
  const { cardOnFile } = (await r.json()) as { cardOnFile: boolean };
  return cardOnFile;
}

/** The card on file (brand + last4), or null if none saved. */
export async function getPaymentMethod(): Promise<{ brand: string; last4: string } | null> {
  const r = await check(await fetch(`${apiBase()}/stripe/payment-method`, { headers: await authHeaders() }));
  const { card } = (await r.json()) as { card: { brand: string; last4: string } | null };
  return card;
}

/**
 * Subscribe using the card already on file (no redirect). Returns ok:true when
 * the subscription is active/trialing; ok:false with code NEEDS_CHECKOUT when
 * the card needs authentication or there's no saved card — caller should then
 * fall back to hosted Checkout.
 */
export async function subscribeWithSavedCard(
  plan: BillingPlan = "monthly",
): Promise<{ ok: boolean; status?: string; code?: string }> {
  const r = await check(
    await fetch(`${apiBase()}/stripe/subscribe`, {
      method: "POST",
      headers: await authHeaders(),
      body: JSON.stringify({ plan }),
    }),
  );
  return (await r.json()) as { ok: boolean; status?: string; code?: string };
}

/** Create a Stripe Customer Portal session (cancel / renew / manage). */
export async function createPortal(): Promise<string> {
  const r = await check(
    await fetch(`${apiBase()}/stripe/portal`, {
      method: "POST",
      headers: await authHeaders(),
    }),
  );
  const { url } = (await r.json()) as { url: string };
  return url;
}
